import { useCallback, useEffect, useRef, useState } from 'react';
import { apiCall } from '../../utils/fetchAPI';
import { MAX_PHOTOS, shrinkForUpload } from '../../utils/media';
import { translateApiMessage } from '../../utils/apiMessages';

let nextKey = 0;

/**
 * The photos of a post or an event while it is being written.
 *
 * Each one is sent as soon as it is picked, so it is on the server by the
 * time the text is — and a photo that fails says so on its own thumbnail
 * without costing the text. The form then sends `uuids`, in the order shown.
 *
 * The preview is the file itself (an object URL), not the server's copy: it
 * shows at once, before the upload has finished.
 */
export function usePhotoUploads() {
  const [photos, setPhotos] = useState([]);
  const previews = useRef(new Set());

  const release = (url) => {
    URL.revokeObjectURL?.(url);
    previews.current.delete(url);
  };

  useEffect(
    () => () => {
      previews.current.forEach((url) => URL.revokeObjectURL?.(url));
      previews.current.clear();
    },
    []
  );

  const patch = (key, changes) =>
    setPhotos((current) => current.map((photo) => (photo.key === key ? { ...photo, ...changes } : photo)));

  const upload = async (key, file) => {
    try {
      const body = new FormData();
      body.append('file', await shrinkForUpload(file));

      const response = await apiCall('/medias', { method: 'POST', body });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = data.violations ? Object.values(data.violations)[0] : data.error;
        patch(key, { status: 'error', error: translateApiMessage(message) || 'Cette photo n’a pas pu être envoyée.' });
        return;
      }

      patch(key, { status: 'done', uuid: data.data.uuid });
    } catch {
      patch(key, { status: 'error', error: 'Cette photo n’a pas pu être envoyée.' });
    }
  };

  const add = useCallback(
    (files) => {
      const room = MAX_PHOTOS - photos.length;
      const picked = Array.from(files || []).slice(0, Math.max(0, room));

      const added = picked.map((file) => {
        const preview = URL.createObjectURL?.(file) ?? '';
        previews.current.add(preview);

        return { key: `photo-${++nextKey}`, preview, status: 'uploading', uuid: null, error: '' };
      });

      setPhotos((current) => [...current, ...added]);
      added.forEach((photo, index) => upload(photo.key, picked[index]));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [photos.length]
  );

  const remove = useCallback((key) => {
    setPhotos((current) => {
      const removed = current.find((photo) => photo.key === key);
      if (removed) release(removed.preview);

      return current.filter((photo) => photo.key !== key);
    });
  }, []);

  const reset = useCallback(() => {
    setPhotos((current) => {
      current.forEach((photo) => release(photo.preview));

      return [];
    });
  }, []);

  return {
    photos,
    add,
    remove,
    reset,
    uuids: photos.filter((photo) => 'done' === photo.status).map((photo) => photo.uuid),
    uploading: photos.some((photo) => 'uploading' === photo.status),
    full: photos.length >= MAX_PHOTOS,
  };
}

export default usePhotoUploads;
